import api from './api';

export interface InterventionImage {
  id: number;
  intervention_id: number;
  image_url: string;
  order: number;
  image_type?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Intervention {
  id: number;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'client_unreachable' | 'rescheduled' | string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  scheduled_date: string;
  completed_date?: string;
  address?: string;
  city?: string;
  commune?: string;
  location_address?: string;
  customer_id: number;
  technician_id?: number;
  product_id?: number;
  contract_id?: number;
  subscription_id?: number;
  equipment_count?: number;
  maintenance_offer_id?: number;
  intervention_type?: string;
  climatiseur_type?: string;
  diagnostic_fee?: number;
  diagnostic_paid?: boolean;
  diagnostic_payment_date?: string;
  is_free_diagnosis?: boolean;
  // Paiement échelonné 50/50
  payment_option?: string;
  total_price?: number;
  second_payment_amount?: number;
  second_payment_status?: string;
  second_payment_date?: string;
  // Confirmation client
  report_submitted_at?: string;
  customer_confirmed?: boolean | null;
  customer_confirmed_at?: string;
  customer_rejection_reason?: string | null;
  // Remboursement
  refund_status?: 'pending' | 'refunded' | 'failed' | null;
  refund_amount?: number;
  refund_date?: string;
  refund_canal?: string;
  refund_phone?: string;
  refund_reference?: string;
  created_at: string;
  updated_at: string;
  images?: InterventionImage[];
  intervention_images?: InterventionImage[];
  report_images?: InterventionImage[];
  customer?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    user?: {
      id: number;
      email?: string;
      phone?: string;
    };
  };
  technician?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    profile_image?: string;
    profile_picture?: string;
  };
  maintenance_offer?: {
    id: number;
    title: string;
    price: number;
    description?: string;
    has_active_subscription?: boolean;
  };
  product?: {
    id: number;
    name: string;
    sku: string;
  };
  contract?: {
    id: number;
    reference: string;
  };
  rating?: number;
}

export interface InterventionFilters {
  status?: string;
  priority?: string;
  customer_id?: number;
  technician_id?: number;
  product_id?: number;
  contract_id?: number;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface InterventionsResponse {
  success: boolean;
  data: {
    interventions: Intervention[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class InterventionsService {
  // Récupérer la liste des interventions
  async getInterventions(filters: InterventionFilters = {}): Promise<InterventionsResponse> {
    const response = await api.get<InterventionsResponse>('/interventions', { params: filters });
    return response.data;
  }

  // Récupérer une intervention par son ID
  async getInterventionById(id: number): Promise<Intervention> {
    const response = await api.get<{ success: boolean; data: Intervention }>(`/interventions/${id}`);
    return response.data.data;
  }

  // Créer une nouvelle intervention
  async createIntervention(interventionData: Partial<Intervention>): Promise<Intervention> {
    const response = await api.post<{ success: boolean; data: Intervention }>('/interventions', interventionData);
    return response.data.data;
  }

  // Mettre à jour une intervention
  async updateIntervention(id: number, interventionData: Partial<Intervention>): Promise<Intervention> {
    const response = await api.put<{ success: boolean; data: Intervention }>(`/interventions/${id}`, interventionData);
    return response.data.data;
  }

  // Supprimer une intervention
  async deleteIntervention(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/interventions/${id}`);
    return response.data;
  }

  // Mettre à jour le statut d'une intervention
  async updateStatus(id: number, status: string): Promise<Intervention> {
    const response = await api.patch<{ success: boolean; data: Intervention }>(`/interventions/${id}/status`, { status });
    return response.data.data;
  }

  // Assigner un technicien à une intervention
  async assignTechnician(id: number, technicianId: number): Promise<Intervention> {
    const response = await api.patch<{ success: boolean; data: Intervention }>(`/interventions/${id}/assign`, { technician_id: technicianId });
    return response.data.data;
  }

  // Obtenir des suggestions de techniciens pour une intervention
  async suggestTechnicians(id: number, maxResults: number = 10): Promise<any> {
    const response = await api.post(`/interventions/${id}/suggest-technicians`, { max_results: maxResults });
    return response.data;
  }

  // Assigner automatiquement le meilleur technicien
  async autoAssignIntervention(id: number): Promise<any> {
    const response = await api.post(`/interventions/${id}/auto-assign`);
    return response.data;
  }

  // Envoyer un lien de paiement pour l'offre d'entretien
  async sendPaymentLink(interventionId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/interventions/${interventionId}/send-payment-link`);
    return response.data;
  }

  // Reprogrammer une intervention
  async reschedule(interventionId: number, scheduledDate: string): Promise<any> {
    const response = await api.post(`/interventions/${interventionId}/reschedule`, {
      scheduled_date: scheduledDate
    });
    return response.data;
  }

  // Rembourser une intervention annulée via Mobile Money (FineoPay Payout)
  async refundPayout(interventionId: number, data: { canal: string; clientAccount?: string; amount?: number; reason?: string }): Promise<{ success: boolean; message: string; data?: any }> {
    const response = await api.post<{ success: boolean; message: string; data?: any }>(`/interventions/${interventionId}/refund-payout`, data);
    return response.data;
  }

  // Vérifier et synchroniser le statut du remboursement FineoPay Payout
  async checkRefundStatus(interventionId: number): Promise<{ success: boolean; message: string; data?: any }> {
    const response = await api.post<{ success: boolean; message: string; data?: any }>(`/interventions/${interventionId}/check-refund-status`);
    return response.data;
  }
}

export const interventionsService = new InterventionsService();
